import React, { useState } from 'react';
import { Sparkles, MapPin, Heart, Coffee, Palette, Utensils, Leaf, Theater, DollarSign, Lightbulb, RefreshCw, X, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const categoryIcons = {
  adventure: Sparkles,
  relaxed: Coffee,
  creative: Palette,
  foodie: Utensils,
  wellness: Leaf,
  cultural: Theater
};

const categoryColors = {
  adventure: 'bg-orange-100 text-orange-700 border-orange-200',
  relaxed: 'bg-blue-100 text-blue-700 border-blue-200',
  creative: 'bg-purple-100 text-purple-700 border-purple-200',
  foodie: 'bg-red-100 text-red-700 border-red-200',
  wellness: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cultural: 'bg-indigo-100 text-indigo-700 border-indigo-200'
};

const costLabels = {
  free: { label: 'Free', color: 'text-emerald-600' },
  budget: { label: '$', color: 'text-emerald-600' },
  moderate: { label: '$$', color: 'text-amber-600' },
  splurge: { label: '$$$', color: 'text-rose-600' }
};

const DatePlanningAssistant = ({ matchUserId, matchName, onClose, isModal = true }) => {
  const [loading, setLoading] = useState(false);
  const [dateIdeas, setDateIdeas] = useState(null);
  const [sharedInterests, setSharedInterests] = useState([]);
  const [customPreferences, setCustomPreferences] = useState('');
  const [selectedIdea, setSelectedIdea] = useState(null);

  const generateDateIdeas = async () => {
    setLoading(true);
    try {
      const response = await axios.post('/matches/date-ideas', {
        match_user_id: matchUserId,
        custom_preferences: customPreferences
      });
      
      setDateIdeas(response.data.date_ideas);
      setSharedInterests(response.data.shared_interests || []);
      toast.success('Date ideas generated!');
    } catch (error) {
      console.error('Error generating date ideas:', error);
      toast.error('Failed to generate date ideas');
    } finally {
      setLoading(false);
    }
  };

  const regenerateIdeas = () => {
    setDateIdeas(null);
    setSelectedIdea(null);
    generateDateIdeas();
  };

  const content = (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center">
            <Heart className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-800">Date Planning Assistant</h2>
            <p className="text-sm text-gray-500">Perfect date ideas for you & {matchName}</p>
          </div>
        </div>
        {isModal && onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-date-assistant"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        )}
      </div>

      {/* Initial State - Generate Ideas */}
      {!dateIdeas && !loading && (
        <div className="text-center py-8">
          <div className="w-20 h-20 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-10 h-10 text-emerald-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Ready to plan your perfect date?</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Our AI will analyze your shared interests and create personalized date ideas just for you two.
          </p>
          
          {/* Optional preferences input */}
          <div className="max-w-md mx-auto mb-6">
            <label className="block text-sm font-medium text-gray-700 text-left mb-2">
              Any specific preferences? (optional)
            </label>
            <input
              type="text"
              value={customPreferences}
              onChange={(e) => setCustomPreferences(e.target.value)}
              placeholder="e.g., outdoor activities, vegan-friendly, evening dates..."
              className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              data-testid="date-preferences-input"
            />
          </div>

          <button
            onClick={generateDateIdeas}
            className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-all shadow-lg hover:shadow-xl flex items-center space-x-2 mx-auto"
            data-testid="generate-date-ideas-btn"
          >
            <Sparkles className="w-5 h-5" />
            <span>Generate Date Ideas</span>
          </button>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-emerald-200 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Creating perfect date ideas...</p>
          <p className="text-gray-400 text-sm mt-1">Analyzing your shared interests</p>
        </div>
      )}

      {/* Date Ideas List */}
      {dateIdeas && !selectedIdea && (
        <div>
          {/* Shared Interests Banner */}
          {sharedInterests.length > 0 && (
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-6 border border-emerald-100">
              <p className="text-sm font-medium text-emerald-800 mb-2">Your shared interests:</p>
              <div className="flex flex-wrap gap-2">
                {sharedInterests.map((interest, idx) => (
                  <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm text-emerald-700 border border-emerald-200">
                    {interest}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Ideas Grid */}
          <div className="space-y-3">
            {dateIdeas.map((idea, index) => {
              const CategoryIcon = categoryIcons[idea.category] || Sparkles;
              const categoryStyle = categoryColors[idea.category] || categoryColors.relaxed;
              const cost = costLabels[idea.estimated_cost] || costLabels.moderate;
              
              return (
                <div
                  key={index}
                  onClick={() => setSelectedIdea(idea)}
                  className="bg-white border border-gray-100 rounded-xl p-4 hover:border-emerald-200 hover:shadow-md transition-all cursor-pointer group"
                  data-testid={`date-idea-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryStyle} border`}>
                        <CategoryIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="font-semibold text-gray-800">{idea.title}</h4>
                          <span className={`text-sm font-medium ${cost.color}`}>{cost.label}</span>
                        </div>
                        <p className="text-sm text-gray-500 line-clamp-2">{idea.description}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-emerald-500 transition-colors mt-2" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Regenerate Button */}
          <div className="mt-6 text-center">
            <button
              onClick={regenerateIdeas}
              className="px-4 py-2 text-emerald-600 hover:text-emerald-700 font-medium flex items-center space-x-2 mx-auto hover:bg-emerald-50 rounded-lg transition-colors"
              data-testid="regenerate-ideas-btn"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Generate New Ideas</span>
            </button>
          </div>
        </div>
      )}

      {/* Selected Idea Detail */}
      {selectedIdea && (
        <div>
          <button
            onClick={() => setSelectedIdea(null)}
            className="flex items-center space-x-1 text-emerald-600 hover:text-emerald-700 mb-4"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            <span>Back to all ideas</span>
          </button>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-6 border border-emerald-100">
            {(() => {
              const CategoryIcon = categoryIcons[selectedIdea.category] || Sparkles;
              const categoryStyle = categoryColors[selectedIdea.category] || categoryColors.relaxed;
              const cost = costLabels[selectedIdea.estimated_cost] || costLabels.moderate;
              
              return (
                <>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${categoryStyle} border`}>
                      <CategoryIcon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{selectedIdea.title}</h3>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className={`px-2 py-0.5 rounded-full ${categoryStyle} border capitalize`}>
                          {selectedIdea.category}
                        </span>
                        <span className={`font-medium ${cost.color}`}>{cost.label}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700 mb-6 leading-relaxed">{selectedIdea.description}</p>

                  <div className="space-y-4">
                    <div className="flex items-start space-x-3 bg-white rounded-xl p-4">
                      <Heart className="w-5 h-5 text-rose-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">Why it's perfect for you</p>
                        <p className="text-sm text-gray-600">{selectedIdea.ideal_for}</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-3 bg-white rounded-xl p-4">
                      <Lightbulb className="w-5 h-5 text-amber-500 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-800">Pro tip</p>
                        <p className="text-sm text-gray-600">{selectedIdea.tip}</p>
                      </div>
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          <div className="mt-6 flex space-x-3">
            <button
              onClick={() => setSelectedIdea(null)}
              className="flex-1 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
            >
              See Other Ideas
            </button>
            <button
              onClick={() => {
                toast.success(`Great choice! Plan your ${selectedIdea.title} date!`);
                if (onClose) onClose();
              }}
              className="flex-1 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-600 transition-colors"
              data-testid="choose-date-idea-btn"
            >
              Choose This Date
            </button>
          </div>
        </div>
      )}
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
          {content}
        </div>
      </div>
    );
  }

  return content;
};

export default DatePlanningAssistant;
